import React, { FC, useCallback, useEffect, useMemo, useRef } from 'react';
import InfiniteLoader from 'react-window-infinite-loader';
import { FixedSizeList } from 'react-window';

import { usePlayerContext } from 'application/providers/PlayerProvider';

import { DATA_TEST_IDS, PROMO_PAGE_SIZE, PROMOS_LIST_HEIGHT } from '../../../consts';
import PromoOption from '../../PromoOption';
import { PROMO_LIST_ITEM_HEIGHT, PROMO_LIST_LOADING_ITEM_KEY, PROMO_LIST_PRELOAD_THRESHOLD } from './consts';
import type { PromoListItemData } from '../../types';
import type { PromoVirtualizedListProps } from './types';

import styles from './index.module.scss';

const PromoVirtualizedList: FC<PromoVirtualizedListProps> = ({
  hasNextPage,
  isBulkSelectionActionPending,
  items,
  queryKey,
  onLoadMore,
  onPromoPlaybackToggle,
  onPromoSelectionChange,
}) => {
  const { fileUuid, isLoading, isPaused, pause, resume } = usePlayerContext();
  const listRef = useRef<FixedSizeList | null>(null);

  useEffect(() => {
    const list = listRef.current;

    if (list && typeof list.scrollTo === 'function') {
      list.scrollTo(0);
    }
  }, [queryKey]);

  const listData = useMemo<PromoListItemData>(
    () => ({
      items,
      fileUuid,
      isLoading,
      isPaused,
      isBulkSelectionActionPending,
      onPromoPlaybackToggle,
      onPromoSelectionChange,
      pause,
      resume,
    }),
    [
      fileUuid,
      isLoading,
      isPaused,
      isBulkSelectionActionPending,
      items,
      onPromoPlaybackToggle,
      onPromoSelectionChange,
      pause,
      resume,
    ]
  );
  const itemCount = items.length + Number(hasNextPage);
  const isItemLoaded = useCallback(
    (index: number) => !hasNextPage || index < items.length,
    [hasNextPage, items.length]
  );
  const getItemKey = useCallback(
    (index: number, data: PromoListItemData) => data.items[index]?.id ?? PROMO_LIST_LOADING_ITEM_KEY,
    []
  );

  return (
    <InfiniteLoader
      isItemLoaded={isItemLoaded}
      itemCount={itemCount}
      loadMoreItems={onLoadMore}
      minimumBatchSize={PROMO_PAGE_SIZE}
      threshold={PROMO_LIST_PRELOAD_THRESHOLD}>
      {({ onItemsRendered, ref }) => (
        <FixedSizeList
          ref={(element) => {
            listRef.current = element;
            ref(element);
          }}
          className={styles.listInner}
          data-testid={DATA_TEST_IDS.AUDIO_REPORT_PROMOS_VIRTUAL_LIST}
          height={PROMOS_LIST_HEIGHT}
          itemCount={itemCount}
          itemData={listData}
          itemKey={getItemKey}
          itemSize={PROMO_LIST_ITEM_HEIGHT}
          width="100%"
          onItemsRendered={onItemsRendered}>
          {PromoOption}
        </FixedSizeList>
      )}
    </InfiniteLoader>
  );
};

export default PromoVirtualizedList;
